/**
 * Add Site — full-page wizard that onboards a domain as a managed-DNS zone:
 * eligibility + create → import (scan/review staged records) → publish → delegate
 * (nameservers + live NS-status). Mirrors the Stepper + ActionBar wizard pattern
 * (see `components/resources/AutoDiscoveryWizard.tsx`). Emits to the Owl backend
 * via the `@/api/waf` zone hooks; the manual "add record" step reuses
 * `DnsRecordFormDialog`.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Globe, Plus, RefreshCw, Pencil, Trash2, Copy } from 'lucide-react';
import { Container, PageHeader, ActionBar } from '@vnetwork/ui/layout';
import {
  Card,
  CardBody,
  Stepper,
  Button,
  Input,
  Skeleton,
  Badge,
  CodeBlock,
  IconButton,
  Switch,
  Tooltip,
  TooltipProvider,
} from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, Pagination, type ColumnDef } from '@vnetwork/ui/data';
import { Field } from '@vnetwork/ui/forms';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useCheckZoneEligibility,
  useCreateDnsZone,
  useScanZoneRecords,
  useZoneReview,
  usePublishZone,
  useWafNameservers,
  useNsStatus,
  useRefreshNsStatus,
  useDeleteWafDnsRecord,
  usePatchWafDnsRecord,
  wafExtractError,
  type WafDnsZoneDetail,
  type WafZoneEligibility,
  type WafDnsRecord,
} from '@/api/waf';
import { trackFeatureAction } from '@/lib/analytics-events';
import { DnsRecordFormDialog } from '../dns/DnsRecordFormDialog';
import styles from './WafAddSiteWizard.module.css';

const STEPS = [
  { label: 'Domain', description: 'Add the site' },
  { label: 'Import records', description: 'Review staged DNS' },
  { label: 'Publish', description: 'Activate the site' },
  { label: 'Delegate', description: 'Point your nameservers' },
];

const PAGE_SIZE = 50;
/** Managed-DNS mode for publish (per the add-site flow). */
const DNS_PROVIDER_MANAGED = 1;

/** Multi-CDN ("Boosted") toggle for a staged record — patches `proxied`. */
function StagedMultiCdnCell({
  serviceId,
  zoneId,
  record,
  onChanged,
}: {
  serviceId: string;
  zoneId: string;
  record: WafDnsRecord;
  onChanged: () => void;
}) {
  const { show } = useToast();
  const [on, setOn] = useState(record.proxied);
  useEffect(() => setOn(record.proxied), [record.proxied]);

  const patch = usePatchWafDnsRecord(serviceId, {
    onSuccess: (_d, vars) => {
      show({ tone: 'success', title: vars.patch.proxied ? 'Multi-CDN enabled' : 'Multi-CDN disabled' });
      trackFeatureAction('waf', 'dns_record', 'toggle');
      onChanged();
    },
    onError: (e) => { setOn(record.proxied); show({ tone: 'danger', title: wafExtractError(e, 'Update failed') }); },
  });

  return (
    <div onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <Switch
        checked={on}
        aria-label="Multi-CDN"
        disabled={!record.proxiable || patch.isPending}
        onCheckedChange={(v) => { setOn(v); patch.mutate({ zoneId, id: record.id, patch: { proxied: v } }); }}
      />
      <span style={{ fontSize: 'var(--vn-font-size-sm)', color: on ? undefined : 'var(--vn-fg-muted)' }}>
        {on ? 'Boosted' : 'Off'}
      </span>
    </div>
  );
}

/** Per-row edit / delete actions for a staged record in the Import step. */
function StagedRecordActions({
  serviceId,
  zoneId,
  zoneName,
  record,
  onChanged,
}: {
  serviceId: string;
  zoneId: string;
  zoneName?: string;
  record: WafDnsRecord;
  onChanged: () => void;
}) {
  const { show } = useToast();
  const [editOpen, setEditOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const del = useDeleteWafDnsRecord(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'DNS record deleted' }); trackFeatureAction('waf', 'dns_record', 'delete'); setConfirmDelete(false); onChanged(); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Delete failed') }),
  });

  return (
    <div onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', gap: 2 }}>
      <TooltipProvider>
        {record.editable && (
          <Tooltip content="Edit" side="top">
            <IconButton variant="ghost" size="sm" aria-label="Edit record" onClick={() => setEditOpen(true)}>
              <Pencil size={14} />
            </IconButton>
          </Tooltip>
        )}
        {record.deletable && (
          <Tooltip content="Delete" side="top">
            <IconButton variant="ghost" size="sm" aria-label="Delete record" onClick={() => setConfirmDelete(true)}>
              <Trash2 size={14} />
            </IconButton>
          </Tooltip>
        )}
      </TooltipProvider>

      {confirmDelete && (
        <ConfirmDialog
          open
          onOpenChange={(o) => { if (!o) setConfirmDelete(false); }}
          title="Delete DNS record?"
          description={`Delete the ${record.type} record "${record.name}"? This cannot be undone.`}
          confirmLabel="Delete"
          destructive
          loading={del.isPending}
          onConfirm={() => del.mutate({ zoneId, id: record.id })}
        />
      )}

      {editOpen && (
        <DnsRecordFormDialog
          serviceId={serviceId}
          zoneId={zoneId}
          zoneName={zoneName}
          record={record}
          open={editOpen}
          onOpenChange={(o) => { setEditOpen(o); if (!o) onChanged(); }}
        />
      )}
    </div>
  );
}

export function WafAddSiteWizard({ serviceId }: { serviceId: string }) {
  const navigate = useNavigate();
  const { show } = useToast();
  const base = `/services/waf/${serviceId}`;

  const [step, setStep] = useState(0);
  const [reached, setReached] = useState(0);
  const [domain, setDomain] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [eligibility, setEligibility] = useState<WafZoneEligibility | null>(null);
  const [zone, setZone] = useState<WafDnsZoneDetail | null>(null);
  const [scanned, setScanned] = useState(false);
  const [reviewPage, setReviewPage] = useState(1);
  const [addOpen, setAddOpen] = useState(false);

  const zoneId = zone?.id;

  const elig = useCheckZoneEligibility(serviceId);
  const createZone = useCreateDnsZone(serviceId);
  const scan = useScanZoneRecords(serviceId);
  // target='' lists ALL staged records (not just CDN-eligible), so records the
  // user adds manually here — plain A/CNAME/etc. — show up after creation too.
  const review = useZoneReview(serviceId, zoneId, { page: reviewPage, pageSize: PAGE_SIZE, target: '' }, (step === 1 || step === 2) && scanned);
  const publish = usePublishZone(serviceId);
  const nameservers = useWafNameservers(serviceId);
  const nsStatus = useNsStatus(serviceId, zoneId, step === 3);
  const refreshNs = useRefreshNsStatus(serviceId);

  const goto = (i: number) => {
    const target = zone ? Math.max(1, i) : i; // never return to Domain once the zone exists
    if (target <= reached) setStep(target);
  };
  const advance = (i: number) => { setStep(i); setReached((r) => Math.max(r, i)); };

  // Resume: arriving with ?zone_id&domain means the zone already exists (a draft
  // from a previous run). Skip eligibility/create and jump straight to Import.
  const [params] = useSearchParams();
  const resumeZoneId = params.get('zone_id') ?? '';
  const resumeDomain = params.get('domain') ?? '';
  const resumedRef = useRef(false);
  useEffect(() => {
    if (resumedRef.current || !resumeZoneId || !resumeDomain) return;
    resumedRef.current = true;
    setZone({
      id: resumeZoneId,
      org_id: '',
      domain_name: resumeDomain,
      zone_status: 0,
      ns_status: 0,
      dns_provider: 0,
      created_at: '',
    });
    setScanned(false);
    advance(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeZoneId, resumeDomain]);

  // Step 0 → eligibility (for the resolved root) then create, on a single Continue.
  const addSite = async () => {
    setError(null);
    const domain_name = domain.trim();
    try {
      setEligibility(await elig.mutateAsync({ domain_name }));
      setZone(await createZone.mutateAsync({ domain_name }));
      trackFeatureAction('waf', 'domain', 'create');
      setScanned(false);
      advance(1);
    } catch (e) {
      setError(wafExtractError(e, 'Could not add this site.'));
    }
  };

  // On entering the Import step, scan the domain's current DNS once per zone.
  const scanRef = useRef<string | null>(null);
  useEffect(() => {
    if (step === 1 && zoneId && scanRef.current !== zoneId) {
      scanRef.current = zoneId;
      scan.mutate({ zoneId }, { onSettled: () => setScanned(true) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, zoneId]);

  const doPublish = async () => {
    if (!zoneId) return;
    try {
      setZone(await publish.mutateAsync({ zoneId, dnsProvider: DNS_PROVIDER_MANAGED }));
      trackFeatureAction('waf', 'domain', 'edit');
      advance(3);
    } catch (e) {
      show({ tone: 'danger', title: wafExtractError(e, 'Publish failed') });
    }
  };

  const finish = () => {
    show({ tone: 'success', title: `${zone?.domain_name ?? 'Site'} added` });
    navigate(`${base}/dns-records`);
  };

  const records = review.data?.records ?? [];
  const reviewPages = review.data?.pagination?.total_pages ?? 1;
  const recordCols = useMemo<ColumnDef<WafDnsRecord, unknown>[]>(
    () => [
      { id: 'type', header: 'Type', enableSorting: false, cell: ({ row }) => <Badge tone="neutral" appearance="subtle" size="sm">{row.original.type}</Badge> },
      { id: 'name', header: 'Name', enableSorting: false, cell: ({ row }) => <span style={{ fontWeight: 500 }}>{row.original.name}</span> },
      { id: 'ttl', header: 'TTL', enableSorting: false, cell: ({ row }) => row.original.ttl },
      {
        id: 'values',
        header: 'Values',
        enableSorting: false,
        cell: ({ row }) => (
          <span style={{ fontFamily: 'var(--vn-font-mono, ui-monospace, monospace)', fontSize: 13 }}>
            {row.original.values.join(', ') || '—'}
          </span>
        ),
      },
      {
        id: 'multi-cdn',
        header: 'Multi-CDN',
        enableSorting: false,
        cell: ({ row }) => (
          <StagedMultiCdnCell
            serviceId={serviceId}
            zoneId={zoneId ?? ''}
            record={row.original}
            onChanged={() => { void review.refetch(); }}
          />
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => (
          <StagedRecordActions
            serviceId={serviceId}
            zoneId={zoneId ?? ''}
            zoneName={zone?.domain_name}
            record={row.original}
            onChanged={() => { void review.refetch(); }}
          />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [serviceId, zoneId, zone?.domain_name],
  );
  // Read-only columns (Type/Name/Values/TTL) for the publish review — no toggles
  // or row actions, just what will be published.
  const summaryCols = useMemo(() => recordCols.slice(0, 4), [recordCols]);

  // ---- Action bar (per step) ----
  const actions = () => {
    if (step === 0) {
      return (
        <ActionBar
          start={<Button variant="ghost" onClick={() => navigate(`${base}/domains`)}>Cancel</Button>}
          end={
            <Button disabled={!domain.trim() || elig.isPending || createZone.isPending} onClick={addSite}>
              {elig.isPending || createZone.isPending ? 'Adding…' : 'Continue'}
            </Button>
          }
        />
      );
    }
    if (step === 1) {
      return <ActionBar end={<Button onClick={() => advance(2)}>Continue</Button>} />;
    }
    if (step === 2) {
      return (
        <ActionBar
          start={<Button variant="ghost" onClick={() => setStep(1)}>Back</Button>}
          end={
            <Button disabled={publish.isPending} onClick={doPublish}>
              {publish.isPending ? 'Publishing…' : 'Publish site'}
            </Button>
          }
        />
      );
    }
    return <ActionBar end={<Button onClick={finish}>Done</Button>} />;
  };

  return (
    <Container size="lg">
      <PageHeader icon={<Globe size={22} />} title="Add site" description="Onboard a domain as a managed-DNS zone." />

      <Card>
        <CardBody>
          <div className={styles.body}>
            <Stepper steps={STEPS} current={step} reached={reached} onStepClick={goto} />

            {step === 0 && (
              <div className={styles.step}>
                <Field label="Domain" description="The apex domain or FQDN to onboard, e.g. example.com.">
                  {(p) => (
                    <Input
                      {...p}
                      placeholder="example.com"
                      value={domain}
                      onChange={(e) => setDomain(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && domain.trim()) addSite(); }}
                      autoFocus
                    />
                  )}
                </Field>
                {error && <Alert tone="danger" title="Couldn't add the site">{error}</Alert>}
              </div>
            )}

            {step === 1 && (
              <div className={styles.step}>
                <Alert tone="info" title={`Importing DNS for ${zone?.domain_name ?? ''}`}>
                  We scanned the domain's current DNS and staged the records below. Add any missing records, then continue to publish.
                </Alert>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--vn-space-2)' }}>
                  <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>Staged records</span>
                  <Button variant="secondary" size="sm" startIcon={<Plus size={15} />} onClick={() => setAddOpen(true)}>
                    Add record
                  </Button>
                </div>
                {scan.isPending || !scanned || review.isPending ? (
                  <Skeleton style={{ height: 220 }} />
                ) : (
                  <>
                    <DataTable
                      columns={recordCols}
                      data={records}
                      getRowId={(r) => r.id}
                      emptyState={<EmptyState title="No records staged" description="Nothing was found to import. Add a record manually, or continue." />}
                      footer={review.data?.pagination ? `${review.data.pagination.total} record(s)` : undefined}
                    />
                    {reviewPages > 1 && (
                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Pagination page={reviewPage} pageCount={reviewPages} onPageChange={setReviewPage} />
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {step === 2 && (
              <div className={styles.step}>
                <Alert tone="info" title="Publish this site">
                  Publishing activates the zone so traffic can be served. You can still edit records afterwards.
                </Alert>
                <div className={styles.summary}>
                  <div><span className={styles.k}>Domain</span><span className={styles.v}>{zone?.domain_name}</span></div>
                  {eligibility?.root_domain && (
                    <div><span className={styles.k}>Root domain</span><span className={styles.v}>{eligibility.root_domain}</span></div>
                  )}
                  <div><span className={styles.k}>Records</span><span className={styles.v}>{review.data?.pagination?.total ?? records.length}</span></div>
                </div>

                <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>Records to publish</span>
                {review.isPending ? (
                  <Skeleton style={{ height: 180 }} />
                ) : (
                  <>
                    <DataTable
                      columns={summaryCols}
                      data={records}
                      getRowId={(r) => r.id}
                      emptyState={<EmptyState title="No records" description="This zone has no records to publish yet." />}
                      footer={review.data?.pagination ? `${review.data.pagination.total} record(s)` : undefined}
                    />
                    {reviewPages > 1 && (
                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Pagination page={reviewPage} pageCount={reviewPages} onPageChange={setReviewPage} />
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {step === 3 && (
              <div className={styles.step}>
                <Alert tone="info" title="Point your nameservers">
                  At your domain registrar, replace the nameservers with the ones below. Delegation can take a while to propagate.
                </Alert>
                {nameservers.isPending ? (
                  <Skeleton style={{ height: 120 }} />
                ) : (
                  <div className={styles.nsList}>
                    {(nameservers.data ?? []).map((ns) => (
                      <div key={ns.host} className={styles.nsRow}>
                        <CodeBlock code={ns.host} />
                        {ns.region && <span className={styles.nsRegion}>{ns.region}</span>}
                        <TooltipProvider>
                          <Tooltip content="Copy nameserver" side="top">
                            <IconButton
                              variant="ghost"
                              size="sm"
                              aria-label={`Copy ${ns.host}`}
                              onClick={() => {
                                void navigator.clipboard.writeText(ns.host);
                                show({ tone: 'success', title: 'Nameserver copied' });
                              }}
                            >
                              <Copy size={14} />
                            </IconButton>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    ))}
                  </div>
                )}
                <div className={styles.nsStatus}>
                  <span className={styles.k}>Delegation</span>
                  {nsStatus.isPending ? (
                    <Skeleton style={{ height: 22, width: 120 }} />
                  ) : nsStatus.data?.matched ? (
                    <Badge tone="success" appearance="subtle">Delegated</Badge>
                  ) : (
                    <Badge tone="warning" appearance="subtle">
                      Not delegated yet{nsStatus.data?.status ? ` (${nsStatus.data.status})` : ''}
                    </Badge>
                  )}
                  <Button
                    variant="secondary"
                    size="sm"
                    startIcon={<RefreshCw size={14} className={refreshNs.isPending ? styles.spin : undefined} />}
                    disabled={refreshNs.isPending || !zoneId}
                    onClick={() => zoneId && refreshNs.mutate({ zoneId })}
                  >
                    Re-check
                  </Button>
                </div>
              </div>
            )}

            {actions()}
          </div>
        </CardBody>
      </Card>

      {zoneId && (
        <DnsRecordFormDialog
          serviceId={serviceId}
          zoneId={zoneId}
          zoneName={zone?.domain_name}
          open={addOpen}
          onOpenChange={(o) => { setAddOpen(o); if (!o) review.refetch(); }}
        />
      )}
    </Container>
  );
}

export default WafAddSiteWizard;
